import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';

import { PACK_HANDOFF_GRACE_MS, PACK_HANDOFF_PARAM, packHandoffPending, packWalkthroughPending } from './pack-handoff';

/**
 * Local busy state for the window between accepting a pack and the announcement turn becoming visible
 * on the conversation (see `lib/pack-handoff`).
 *
 * Armed off the marker the accept landing's CTA puts on the URL — the only evidence the chat has that
 * this arrival came from an accept rather than from a user opening an old thread. It is consumed once:
 * the param is stripped immediately, so a reload or a shared URL spins nothing, and the flag then lives
 * in state until it expires. A real turn taking over (`remoteTurnActive`, a streamed row) outranks it
 * at the render site, so nothing has to clear it.
 */
export function usePackHandoff(conversationId?: string | null): boolean {
  const router = useRouter();
  const [armedAt, setArmedAt] = useState<number | null>(null);
  const consumed = useRef(false);
  const [handoffConversationId, setHandoffConversationId] = useState<string | null>(null);
  const [leftHandoffConversation, setLeftHandoffConversation] = useState(false);
  // The conversation the marker URL names (`packChatHref`). The accept forwards in-app, so the chat may
  // still show the thread this tab had selected for a render or two before `ChatSurface` selects this one.
  // That earlier thread is not the arrival: it must neither get the wait nor be adopted as it.
  const namedConversationRef = useRef<string | null>(null);
  if (!consumed.current && router.query[PACK_HANDOFF_PARAM] === '1') {
    namedConversationRef.current = typeof router.query.conversation === 'string' ? router.query.conversation : null;
  }
  const named = namedConversationRef.current;

  useEffect(() => {
    if (!router.isReady || consumed.current) return;
    if (router.query[PACK_HANDOFF_PARAM] !== '1') return;
    consumed.current = true;
    setArmedAt(Date.now());
    // The marker has TWO copies and both have to go, or "consumed on arrival" is only half true.
    //
    // The ADDRESS is rewritten with the History API, which is what keeps `?chat`/`?conversation` on it:
    // `useChatUrlSync` writes those two the same way and deliberately keeps them off the router's query,
    // so a `router.replace` that built its address out of that query would drop them.
    //
    // The ROUTER's copy then has to go too, with the address we just wrote passed as the explicit `as` so
    // nothing is dropped from it. `history.replaceState` cannot touch that copy, and leaving it is what
    // puts a consumed marker back on screen: every writer that rebuilds a chat URL from `...router.query`
    // carries it forward, and a reader that closes the chat and reopens it mounts a fresh instance of this
    // hook, which reads the stale param as a brand-new accept and arms the loader again for 90s.
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      url.searchParams.delete(PACK_HANDOFF_PARAM);
      window.history.replaceState(window.history.state, '', url.href);
      const query = { ...router.query };
      delete query[PACK_HANDOFF_PARAM];
      void router.replace({ pathname: router.pathname, query }, `${url.pathname}${url.search}`, { shallow: true });
    }
  }, [router, router.isReady, router.query]);

  // Re-evaluate once at the horizon — the flag has no other end, so without this the indicator it
  // switched on would never switch off.
  useEffect(() => {
    if (armedAt === null) return undefined;
    const timer = setTimeout(() => setArmedAt(null), PACK_HANDOFF_GRACE_MS);
    return () => clearTimeout(timer);
  }, [armedAt]);

  // Render the marker immediately. Arming only in the effect leaves the first render empty, whose
  // sibling minimize effect can collapse the chat before this state update is applied.
  const pending = packHandoffPending(armedAt) || (!consumed.current && router.query[PACK_HANDOFF_PARAM] === '1');
  const conversationChanged = handoffConversationId !== null && handoffConversationId !== (conversationId ?? null);
  // Not adopted yet, and another thread is on screen: wait for the named one without latching anything.
  const notYetOnNamed = handoffConversationId === null && named !== null && !!conversationId && conversationId !== named;
  useEffect(() => {
    if (!pending || leftHandoffConversation) return;
    if (conversationChanged) setLeftHandoffConversation(true);
    // A bootstrap accept has no id yet. Its first persisted id adopts the same arrival. A named one adopts
    // only that id.
    else if (handoffConversationId === null && conversationId && !notYetOnNamed) setHandoffConversationId(conversationId);
  }, [pending, leftHandoffConversation, conversationChanged, notYetOnNamed, handoffConversationId, conversationId]);
  // Gate this render as well as latching the switch: another transcript must never paint this wait,
  // supply its walkthrough turn, or revive it when the reader comes back before the timer expires.
  return pending && !leftHandoffConversation && !conversationChanged && !notYetOnNamed;
}

/** Follow this arrival once; a later user turn must never revive a finished pack placeholder. */
export function usePackWalkthroughPending(state: Parameters<typeof packWalkthroughPending>[0]): boolean {
  const [settled, setSettled] = useState(false);
  const finished = state.userTurnStarted || state.stopped || state.failed || (state.turnSeen && !state.turnActive);
  useEffect(() => {
    if (state.handoffPending && finished) setSettled(true);
  }, [state.handoffPending, finished]);
  return packWalkthroughPending({ ...state, settled });
}
